import { Fragment, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useLocation } from 'react-router-dom';
//constants
import { timing } from 'constants/ui.constants';
// @mui
import { Stack } from '@mui/system';
// components
import { Page } from 'theme/elements/Page';
import { CenteredNarrowContent } from 'theme/elements/Content';
// reducers
import { useAppSelector, useAppDispatch } from 'hooks/redux.hooks';
import { getHistory, togglePage } from 'state/reducers/ui.reducers';
import MobileNavbar from 'sections/layout/MobileNavbar';
import { Convert } from 'constants/convert.constants';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { FormHeader1, getUrlBack } from 'theme/elements/Headers';
import { getConvertById } from 'services/convert.services';
import { FormsStack } from 'theme/elements/Forms';
import { InfoCard } from 'theme/elements/Cards';
import { Paragraph1 } from 'theme/elements/Paragraphs';
import { format, isValid } from 'date-fns';
import { getFormattedNumber } from 'utils';

export default function ConvertProfile() {
	const t = useAppSelector(selectTranslations);
	const location = useLocation();
	const dispatch = useAppDispatch();
	const [openCancelDialog, setOpenCancelDialog] = useState(false);
	//get location
	const paths = location.pathname.split('/');
	const lastPath = paths[paths.length - 1];
	const history = useAppSelector(getHistory);
	const urlback = getUrlBack(location.pathname, history[history.length - 1]);
	const [conversionResult, setConversionResult] = useState<(Record<string, any> & Convert) | undefined>();
	const prefixFields = 'page.convert.create.step.3.';
	const prefixHeaderFields = 'page.convert.create.step.1.';

	const getContact = useMemo(() => {
		if (_.has(conversionResult, 'pendingQuote.contact')) {
			return (
				_.get(conversionResult, 'pendingQuote.contact.firstName') +
				' ' +
				_.get(conversionResult, 'pendingQuote.contact.lastName')
			);
		}
		return undefined;
	}, [conversionResult]);

	useEffect(() => {
		dispatch(getConvertById(lastPath)).then((res) => {
			if (res) {
				setConversionResult({
					...res,
				});
			}
		});
	}, [lastPath]);

	const finalRate = useMemo(() => {
		if (!_.isNil(_.get(conversionResult, 'pendingQuote.altshulerRate'))) {
			return _.get(conversionResult, 'pendingQuote.altshulerRate');
		}

		return '';
	}, [conversionResult]);

	return (
		<Fragment>
			<MobileNavbar
				handleBack={() => {
					dispatch(togglePage(urlback));
				}}
				handleCancel={() => {
					setOpenCancelDialog(true);
				}}
			/>
			<Page>
				<Stack spacing={{ sm: 5, xs: 1 }}>
					<CenteredNarrowContent>
						<Fragment>
							{conversionResult && (
								<FormsStack>
									<InfoCard>
										<FormHeader1 title={t(`${prefixHeaderFields}title`)} />
										<Paragraph1
											title={t(prefixFields + 'created_date.text')}
											text={
												isValid(new Date(_.get(conversionResult, 'createdAt') || 0))
													? format(
															new Date(_.get(conversionResult, 'createdAt') || 0),
															timing.DATE
													  )
													: _.get(conversionResult, 'createdAt')
											}
										/>
										{!_.isNil(_.get(conversionResult, 'pendingQuote.dueDate')) ? (
											<Paragraph1
												title={t(prefixFields + 'settlement_date.text')}
												text={
													isValid(
														new Date(_.get(conversionResult, 'pendingQuote.dueDate') || 0)
													)
														? format(
																new Date(
																	_.get(conversionResult, 'pendingQuote.dueDate') || 0
																),
																timing.DATE
														  )
														: _.get(conversionResult, 'pendingQuote.dueDate')
												}
											/>
										) : (
											<></>
										)}

										<Paragraph1
											title={t(prefixFields + 'sold.text')}
											text={
												getFormattedNumber(_.get(conversionResult, 'pendingQuote.altshulerSellAmount')) +
												' ' +
												conversionResult.pendingQuote.clientSellCurrency
											}
										/>
										<Paragraph1
											title={t(prefixFields + 'bought.text')}
											text={
												getFormattedNumber(_.get(conversionResult, 'pendingQuote.altshulerBuyAmount')) +
												' ' +
												conversionResult.pendingQuote.clientBuyCurrency
											}
										/>
										<Paragraph1 title={t(prefixFields + 'final_rate.text')} text={finalRate} />
										{!_.isEmpty(conversionResult.status) ? (
											<Paragraph1
												title={t(prefixFields + 'status.text')}
												text={t(
													prefixFields +
														'status.' +
														_.snakeCase(String(conversionResult.status)) +
														'.text'
												)}
											/>
										) : (
											<></>
										)}
										<Paragraph1
											title={t(prefixFields + 'reference.text')}
											text={_.get(conversionResult, 'reference')}
										/>
										{getContact ? (
											<Paragraph1
												title={t(prefixFields + 'created_by.text')}
												text={getContact || ''}
											/>
										) : (
											<></>
										)}
									</InfoCard>
								</FormsStack>
							)}
						</Fragment>
					</CenteredNarrowContent>
				</Stack>
			</Page>
		</Fragment>
	);
}
